import { useSelector } from 'react-redux';

import { RequestOffer } from 'connect/saga/handlers/offer/request-offer';
import { RequestReferralPromotion } from 'connect/saga/handlers/offer/request-referral-promotion';
import {
  selectCJTags,
  selectDataClinic,
  selectDataMatch,
  selectDataPatient,
  selectDataProfileLink,
  selectDataRequestedAppointment,
} from 'connect/selectors';
import { CheckoutStateHandler } from 'pages/checkout-v3/connect/state-handlers';
import { CheckoutState } from 'pages/checkout-v3/connect/types';
import { getInNetworkStatus } from 'pages/common/helpers/common-helpers';
import { selectOngoingCareNoMatch, selectReasonNoMatch } from 'pages/legacy-checkout/connect/selectors';
import { googlePlaceActionLink } from 'utils/google';

export const useAppointmentRequestData = () => {
  const checkoutState = useSelector<any, CheckoutState>(CheckoutStateHandler.selector);
  const patient = useSelector(selectDataPatient);
  const requestedAppointment = useSelector(selectDataRequestedAppointment);
  const match = useSelector(selectDataMatch);
  const clinic = useSelector(selectDataClinic);
  const cjTags = useSelector(selectCJTags);

  const referralPromotion = useSelector(RequestReferralPromotion.value!.selector);
  const offer = useSelector(RequestOffer.value!.selector);

  const directLink = useSelector(selectDataProfileLink);

  const {
    rwgToken,
    merchantId,
  } = googlePlaceActionLink.getConversionData();

  const { slots } = clinic;
  const requestedSlot = slots.find((slot: any) => slot.id === requestedAppointment.slotId);

  const providerIdForSlot = requestedSlot?.provider?.id;
  const providerForSlot = clinic.providers.find((provider: any) => provider.id === providerIdForSlot);

  const appointmentProvider = providerForSlot || requestedAppointment?.provider || clinic.providers.at(0);

  // providerId is required for an NPR
  // If the providerId is not set, use the first provider in the clinic
  const providerId = appointmentProvider.id;
  const {
    insurance: insuranceState,
    patient: patientState,
  } = checkoutState;

  // If providerId is not set, assume anyProviderSelected is true
  const anyProviderSelected = requestedAppointment?.providerId
    ? requestedAppointment?.anyProviderSelected
    : true;

  const inNetwork = getInNetworkStatus(
    insuranceState?.plan?.id,
    appointmentProvider?.doctor?.insurancePlans,
  );

  // Create insurance data from checkout state
  const insuranceData = {
    provider: insuranceState?.provider?.name || '',
    providerId: insuranceState?.provider?.id || '',
    providerPlan: insuranceState?.plan?.name || '',
    planId: insuranceState?.plan?.id || '',
    memberId: insuranceState?.memberId || '',
    groupNumber: insuranceState?.groupId,
    isPrimaryHolder: insuranceState?.isPrimaryMember,
    inNetwork,
  };

  const requestDataMatch = match?.id
    ? {
      id: match.id,
      specialty: match.specialty?.name,
      state: match.location?.address?.administrative_area_level_1,
      locality: match.location?.address?.locality,
    }
    : undefined;

  const ongoingCareNoMatch = useSelector(selectOngoingCareNoMatch);
  // The selector is named reasonNoMatch, but it's actually the selectedTreatments
  const selectedTreatmentsNoMatch = useSelector(selectReasonNoMatch);

  // Fall back to undefined since the API expects certain values and won't accept null
  const ongoingCare = match?.other?.ongoingCare || ongoingCareNoMatch || undefined;
  const selectedTreatments = match?.other?.selectedTreatments || selectedTreatmentsNoMatch || undefined;

  const requestDataReason = match?.other
    ? {
      overall: match.other.reasonRaw?.overall || undefined,
      subReasons: match.other.subReasons || undefined,
      subReasonIds: match.other.subReasonIds || undefined,
      other: match.other.otherReason || undefined,
      selectedTreatments: selectedTreatments || undefined,
    }
    : undefined;

  const matchDmProps = match?.other?.dmProps;

  const hasActiveOffer = Boolean(offer?.active);

  const requestDataDmProps =
    hasActiveOffer || Object.keys(matchDmProps).length > 0
      ? {
        promo_code: hasActiveOffer ? offer.slug : '',
        referral_code: matchDmProps?.referral_code,
        referral_promo: matchDmProps?.referral_promo,
        referral_promo_id: referralPromotion?.id,
      }
      : undefined;

  const requestData: any = {
    visitedBefore: false,
    source: 'recommendations',
    match: requestDataMatch,
    reason: requestDataReason,
    ongoingCare,
    utmTags: match?.other?.utmTags || {},
    dmProps: requestDataDmProps,
    isReturningPatient: patientState.isReturningPatient,

    cjEvent: cjTags?.event,
    cjSid: cjTags?.sid,
    cjAid: cjTags?.aid,
    cjPid: cjTags?.pid,
    cjLinkOpenedAt: cjTags?.linkOpenedAt
      ? parseInt(cjTags.linkOpenedAt, 10)
      : undefined,
  };

  if (directLink) {
    requestData.source = 'oc_direct';
  }

  // Set googleActions data for users coming from Google Book Online
  if (rwgToken && merchantId) {
    requestData.source = 'google_place_action';
    requestData.googleActions = {
      rwgToken,
      merchantId,
    };
  }

  const apiRequestBody = {
    providerId,
    patient: {
      dateOfBirth: patient.dateOfBirth || null,
      email: patient.email,
      firstName: patient.firstName,
      gender: patient.gender || null,
      lastName: patient.lastName,
      mobilePhone: patient.mobilePhone,
      settings: patient.settings || {},
    },
    requestedSlot: requestedSlot?.id
      ? {
        id: requestedSlot.id,
        date: requestedSlot.date,
        time: requestedSlot.time,
        provider: requestedSlot.provider.id,
        type: requestedSlot.type || 'Opencare Automatic',
        metadata: requestedSlot.metadata,
      }
      : null,
    offer,
    requestData,
    offeredAppointmentTypeId: requestedAppointment?.appointmentTypeId,
    insuranceData,
    funnelConfiguration: match?.funnelConfiguration,
    anyProviderSelected,
  };

  return apiRequestBody;
};
